import { useState, type CSSProperties, type ReactNode } from 'react';
import type { RoomTicketResp, PlayerCount, RuleSet } from '@mj/protocol';
import { useAuth } from '../auth/useAuth';
import { enterOnline } from '../net/onlineSession';
import { RemoteTable } from '../play/remoteTable';
import { useStudioStore } from '../useStore';
import { RULESET_PRESETS, buildPreset } from '../presets';

const ENV = import.meta.env as Record<string, string | undefined>;

const WRAP: CSSProperties = {
  minHeight: '100%',
  padding: 'calc(var(--sp-5) + env(safe-area-inset-top)) var(--sp-4) calc(var(--sp-5) + env(safe-area-inset-bottom))',
  boxSizing: 'border-box',
  color: 'var(--text-hi)',
  fontFamily: 'var(--font-ui)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};
const SHELL: CSSProperties = {
  width: 'min(920px, 100%)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
};
const TOP: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-3)',
};
const TITLE: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(28px, 7vw, 48px)',
  fontWeight: 700,
  color: 'var(--gold-100)',
  textShadow: '0 2px 12px rgba(0,0,0,0.35)',
};
const CARD: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-panel)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-4)',
  boxShadow: 'var(--shadow-2)',
  backdropFilter: 'var(--blur-panel)',
};
const CARD_TITLE: CSSProperties = {
  margin: 0,
  color: 'var(--gold-300)',
  fontSize: 15,
  fontWeight: 700,
};
const GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
  gap: 'var(--sp-4)',
};
const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-3)',
  minHeight: 52,
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
const FIELD: CSSProperties = {
  minHeight: 44,
  boxSizing: 'border-box',
  background: 'var(--surface-well)',
  color: 'var(--text-hi)',
  border: '1px solid var(--border-panel)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 14,
  fontFamily: 'inherit',
};
const LABEL: CSSProperties = {
  color: 'var(--text-mid)',
  fontSize: 13,
};
const ERROR: CSSProperties = {
  color: 'var(--c-loss)',
  background: 'rgba(229,72,77,0.12)',
  border: '1px solid rgba(229,72,77,0.35)',
  borderRadius: 'var(--r-sm)',
  padding: 'var(--sp-3)',
  fontSize: 13,
  lineHeight: 1.5,
};

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ ...CARD, display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <h2 style={CARD_TITLE}>{title}</h2>
      {children}
    </section>
  );
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

async function readErrorBody(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text) return '';
  try {
    const j = JSON.parse(text) as { error?: unknown };
    if (typeof j.error === 'string') return j.error;
  } catch {
    // fall through to raw text
  }
  return text;
}

// Map the matchmaker's HTTP failures + error codes to Japanese. create 429/RATE_LIMITED, join 409/ROOM_STARTED,
// 404/ROOM_NOT_FOUND are the expected room-lifecycle rejections (see hardening plan cluster E / U5).
function roomErrorJa(status: number, code: string): string {
  if (status === 429 || code === 'RATE_LIMITED') return '部屋を作りすぎです。しばらく待って再試行してください';
  if (status === 409 || code === 'ROOM_STARTED') return 'この部屋は既に対局開始済みです';
  if (status === 404 || code === 'ROOM_NOT_FOUND') return '部屋が見つかりません';
  if (status === 401 || code === 'AUTH_REQUIRED') return 'サインインが必要です';
  if (status === 403) return 'この操作は許可されていません';
  return code ? `${code}` : `エラー (${status})`;
}

export function OnlineLobby(props: { onEnterGame: () => void; onBack: () => void }) {
  const auth = useAuth();
  const ruleSet = useStudioStore((s) => s.ruleSet);
  const [players, setPlayers] = useState<PlayerCount>(4);
  // room ruleset chosen at creation: '' = use the 工房 (studio store) ruleset, else a named preset.
  const [presetId, setPresetId] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function postRoom(path: string, body: unknown): Promise<RoomTicketResp> {
    const base = ENV.VITE_SERVER_URL;
    if (!base) throw new Error('VITE_SERVER_URL 未設定');
    const token = await auth.getToken();
    if (!token) throw new Error('未サインイン');
    const res = await fetch(base + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const bodyText = await readErrorBody(res);
      throw new Error(roomErrorJa(res.status, bodyText));
    }
    return (await res.json()) as RoomTicketResp;
  }

  function enter(resp: RoomTicketResp): void {
    const t = new RemoteTable({
      url: resp.wsUrl,
      seatTicket: resp.seatTicket,
      ruleSet: resp.ruleSet,
      players: resp.room.players as PlayerCount,
      mySeat: resp.seat,
      room: resp.room, // seed the waiting room with the code/seats before the first server roomState.
      rejoin: async (code) => {
        const r = await postRoom('/api/rooms/join', { roomCode: code });
        return r.seatTicket;
      },
    });
    enterOnline(t);
    props.onEnterGame();
  }

  async function run(action: () => Promise<void>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // S6: 作成と開始を分離。作成したら即 enter() し待機ロビー (WaitingRoom) へ。空席を CPU で埋めるかは
  // ロビー内のホスト操作 (開始時) で選ぶので、作成時のチェックは無し (botFill は開始時に決まる既定値)。
  const createRoom = () => run(async () => {
    // ルール: pick a preset (or the 工房 ruleset) and normalise its player count to the selected seat count so
    // the room's scoring / seat-wind tables match. The host can still change rules in the waiting lobby.
    const base: RuleSet = (presetId ? buildPreset(presetId) : null) ?? ruleSet;
    const rs: RuleSet = { ...base, players: players as RuleSet['players'] };
    const resp = await postRoom('/api/rooms', { ruleSet: rs, players, botFill: true });
    enter(resp);
  });

  const joinRoom = () => run(async () => {
    const code = roomCode.trim();
    if (!code) throw new Error('部屋コードを入力してください');
    const resp = await postRoom('/api/rooms/join', { roomCode: code });
    enter(resp);
  });

  const signInGoogle = () => run(() => auth.signInGoogle());
  const signOut = () => run(() => auth.signOut());

  return (
    <main className="table-surface" style={WRAP}>
      <div style={SHELL}>
        <header style={TOP}>
          <button type="button" className="btn-secondary" onClick={props.onBack} disabled={busy}>
            ← 戻る
          </button>
          <h1 style={TITLE}>オンライン対戦</h1>
          <span aria-hidden style={{ width: 86 }} />
        </header>

        {error && <div role="alert" style={ERROR}>{error}</div>}

        {auth.loading ? (
          <div style={{ ...CARD, color: 'var(--text-mid)', textAlign: 'center' }}>読み込み中…</div>
        ) : !auth.user ? (
          <Card title="サインイン">
            <div style={{ color: 'var(--text-mid)', fontSize: 14, lineHeight: 1.6 }}>
              オンライン対戦を始めるにはサインインしてください。
            </div>
            <button type="button" className="btn-primary" onClick={signInGoogle} disabled={busy}>
              Google でサインイン
            </button>
          </Card>
        ) : (
          <>
            <section style={{ ...CARD, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: 'var(--text-low)', fontSize: 12 }}>サインイン中</div>
                <div style={{ color: 'var(--text-hi)', fontSize: 15, fontWeight: 700 }}>
                  {auth.user.name ?? auth.user.id}
                </div>
              </div>
              <button type="button" className="btn-secondary" onClick={signOut} disabled={busy}>
                サインアウト
              </button>
            </section>

            <div style={GRID}>
              <Card title="部屋を作る">
                <label style={ROW}>
                  <span style={LABEL}>人数</span>
                  <select
                    value={players}
                    onChange={(e) => setPlayers(Number(e.currentTarget.value) as PlayerCount)}
                    disabled={busy}
                    style={{ ...FIELD, width: 120 }}
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}人</option>)}
                  </select>
                </label>

                <label style={ROW}>
                  <span style={LABEL}>ルール</span>
                  <select
                    value={presetId}
                    onChange={(e) => setPresetId(e.currentTarget.value)}
                    disabled={busy}
                    style={{ ...FIELD, width: 180 }}
                  >
                    <option value="">工房の設定</option>
                    {RULESET_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.name.ja}</option>)}
                  </select>
                </label>

                {/* S6: 空席を CPU で埋めるかは作成時でなく待機ロビーの開始操作で選ぶ (作成=即 CPU 卓を回避)。 */}
                <div style={{ color: 'var(--text-low)', fontSize: 12, lineHeight: 1.6 }}>
                  作成後は待機ロビーで相手の参加を待てます。空席を CPU で埋めて開始するかは開始時に選べます。
                </div>

                <button type="button" className="btn-primary" onClick={createRoom} disabled={busy}>
                  部屋を作成
                </button>
              </Card>

              <Card title="部屋に参加">
                <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                  <span style={LABEL}>部屋コード</span>
                  <input
                    value={roomCode}
                    onChange={(e) => setRoomCode(e.currentTarget.value)}
                    disabled={busy}
                    style={FIELD}
                    placeholder="ROOM CODE"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </label>
                <button type="button" className="btn-primary" onClick={joinRoom} disabled={busy}>
                  参加
                </button>
              </Card>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
